import React from 'react';
import { useSelector } from 'react-redux';
import { ReduxHelpers } from "../../Helpers/ReduxHelpers";
import { saveFileWithDialog, FileTypes } from "../../Utils/FileSaveDialog";

interface RootState {
    refreshLoadedFile: { isLoading: boolean };
    pickAFile: { 
        fileLength: number | null; 
        fileName: string;
        currentDataContents: string;
    };
}

interface Question {
    dataKey: string;
    selectedType: string;
    sensitive?: number;
}

interface DataModel {
    questions: Question[];
}

const SaveAsFormClassFile: React.FC = () => {
    const isLoading = useSelector((state: RootState) => state.refreshLoadedFile.isLoading);
    const currentFileLength = useSelector((state: RootState) => state.pickAFile.fileLength);
    const currentFileName = useSelector((state: RootState) => state.pickAFile.fileName);
    const currentDataContents = useSelector((state: RootState) => state.pickAFile.currentDataContents);

    const { CreateContentsFromStates } = ReduxHelpers();

    async function SaveJsonAsFormClassFile() {
        CreateContentsFromStates();

        const jsonObject = JSON.parse(currentDataContents);

        const classFileName = currentFileName.toLowerCase().replace(".json", ".cs");
        const dataToSave = RenderFormClassFromDataModelFile(jsonObject);

        // Use native save dialog with suggested filename
        await saveFileWithDialog(dataToSave, {
            suggestedName: classFileName,
            types: FileTypes.CSHARP
        });
    }


    function RenderFormClassFromDataModelFile(dataModel: DataModel): string {

        const questions = dataModel.questions.sort(compare);
        const returnValue = WriteClass(questions);

        return returnValue;
    }

    function compare(a: Question, b: Question): number {
        if (a.dataKey < b.dataKey) {
            return -1;
        }
        if (a.dataKey > b.dataKey) {
            return 1;
        }
        return 0;
    }

    function WriteClass(records: Question[]): string {
        const template = `
    using System;
    using System.Collections.Generic;
    using System.Linq;
    using System.Threading.Tasks;
    using AssistPoint.Models;
    using AssistPoint.Services.DigitalForms;
    using AssistPoint.Services.Encryption;

    //This class was auto generated.
    //Change namespace after inclusion in C# project
    namespace AssistPoint.Services.LifeSciences
    {
        //Change classname after inclusion in C# project
        public class TypedFormAnswers : ISensitive
        {
            private IEncryptionService _enc;
            public static int FieldCount = ***FIELDCOUNT***;
        ***FIELDDEFS***

            public void SetEncrypter(IEncryptionService enc)
            {
                _enc = enc;
            }

            public async Task Decrypt()
            {
                if (_enc == null) throw new Exception("Encrypter has not been set. Cannot decrypt FormAnswers");
            ***DECRYPTS***
            }
        }
    }`;

        return template
            .replaceAll("***FIELDDEFS***", WriteFieldDefinitions(records))
            .replaceAll("***DECRYPTS***", WriteDecrypts(records))
            .replaceAll("***FIELDCOUNT***", records.length.toString());
    }

    function WriteDecrypts(records: Question[]): string {
        const toWrite = records.filter(r => r.sensitive === 1);
        let sb = "";

        for (const record of toWrite) {
            sb += WriteDecrypt(record);
        }

        return sb;
    }

    function WriteDecrypt(record: Question): string {
        const updatedValue = ReplaceSpaces(record.dataKey);

        switch (record.selectedType) {
            case "date":
                return `\t\t\t${updatedValue} = await _enc.DecryptDateIfNeeded(${updatedValue});`;
            case "integer":
                return `\t\t\t${updatedValue} = await _enc.DecryptLongIfNeeded(${updatedValue});`;
            case "ssn":
                return `\t\t\t${updatedValue} = await _enc.DecryptStringIfNeeded(${updatedValue});`;
            default:
                return "";
        }
    }

    function WriteFieldDefinitions(records: Question[]): string {
        let sb = "";

        for (const record of records) {
            sb += WriteFieldDefinition(record);
        }

        return sb;
    }

    function WriteFieldDefinition(record: Question): string {
        const template = `
    \t\tpublic ***TYPE*** ***FIELDNAME*** { get; set; }
    \t\t***TYPEDFIELD***`;

        return template
            .replaceAll("***TYPE***", WriteFieldType(record))
            .replaceAll("***FIELDNAME***", ReplaceSpaces(record.dataKey))
            .replaceAll("***TYPEDFIELD***", WriteTypedField(record));
    }

    function WriteFieldType(record: Question): string {
        switch (record.selectedType) {
            case "genentechHubPharmacy":
                return "List<GenentechHubPharmacyDetail>";

            case "currency":
            case "currency-dollars":
            case "number":
                return "decimal?";

            case "date":
                return record.sensitive === 1
                    ? "string"
                    : "DateTime?";

            case "integer":
                return record.sensitive === 1
                    ? "string"
                    : "long?";

            case "checkbox":
            case "checkbox-attestation":
            case "checkbox-list":
            case "dropdownlist":
            case "integer-text":
            case "markdown":
            case "radiogroup":
            case "radiogroup-vertical":
            case "signature":
            case "ssn":
            case "string":
            case "text":
            case "textarea":
            case "usphone":
            case "yes|no":
            case "zip":
                return "string";
            default:
                return "string";
        }
    }

    function WriteTypedField(record: Question): string {
        if (!RequiresTypedField(record)) return "";

        const updatedValue = ReplaceSpaces(record.dataKey);

        switch (record.selectedType) {
            case "checkboxlist":
                return `public List<string> ${updatedValue}_List => ${updatedValue}.Split('|').ToList();`;

            case "currency":
                return `public decimal? ${updatedValue}_Typed => Convert.ToDecimal(${updatedValue});`;

            case "date":
                return `public DateTime? ${updatedValue}_Typed => Convert.ToDateTime(${updatedValue});`;

            case "integer":
                return `public long? ${updatedValue}_Typed => Convert.ToInt64(${updatedValue});`;

            case "yes|no":
            case "checkbox":
            case "attest":
            case "checkbox-attestation":
                return `public bool? ${updatedValue}_Typed => ${ReplaceSpaces(updatedValue)}.AsBool();`;
            default:
                return "";
        }
    }

    function RequiresTypedField(record: Question): boolean {
        return (record.selectedType === "date" && record.sensitive === 1) ||
            (record.selectedType === "integer" && record.sensitive === 1) ||
            (record.selectedType === "decimal" && record.sensitive === 1) ||
            (record.selectedType === "yes|no") ||
            (record.selectedType === "checkbox") ||
            (record.selectedType === "checkboxlist") ||
            (record.selectedType === "checkbox-attestation") ||
            (record.selectedType === "attest");
    }

    function ReplaceSpaces(value: string): string {
        return value?.replaceAll(" ", "_");
    }

    return (
        <button disabled={currentFileLength == null || isLoading} onClick={SaveJsonAsFormClassFile} title="Save As Form Class">
            Save As Form Class
        </button>
    );
};

export default SaveAsFormClassFile;